/* ════════════════════════════════════════════════════════════════════════
   QUICK CAPTURE ROUTER — rule-based classifier that decides which Home OS
   module a raw input belongs to (text + optional attachments).
   Returns:
     {
       module:           "supplies" | "meals" | "money" | "health" |
                         "calendar" | "documents" | "cleaning" |
                         "family" | "mood" | "chores",
       moduleLabel:      string (display label),
       category:         task.category mirror (Supplies / Meals / …),
       confidence:       "high" | "medium" | "low",
       action:           "add-supply-low" | "add-shopping" | "add-task"
                         | "add-document" | "create-reminder" | "add-mood",
       actionLabel:      string,
       extracted: {
         item, date, time, person, supplyCategory, dueRelative, amount,
       },
       reason: string  // why we picked this module
     }
   ════════════════════════════════════════════════════════════════════════ */

const PERSONS = ["mum", "mom", "dad", "lily", "max", "rosie", "jobe", "jess", "kids", "teen", "baby", "everyone"];

/* Module routing rules. Order matters: first match wins. */
const RULES = [
  // ── SUPPLIES (broad) ─────────────────────────────────────────────
  { module: "supplies", category: "Supplies", action: "add-supply-low", reason: "Sounds like household supplies",
    keywords: [
      "toilet paper", "nappies", "nappy", "diaper", "bin liner", "bin bags",
      "dishwasher tablet", "laundry liquid", "shampoo", "toothpaste",
      "surface spray", "kitchen spray", "batteries", "light bulb",
      "cat litter", "cat food", "dog food", "pet food",
      "school supplies", "glue stick", "stationery", "art supplies",
    ],
  },
  { module: "supplies", category: "Supplies", action: "add-supply-low", reason: "Marks an item low / out / running out",
    patterns: [
      /\b(low|running low|out of|nearly out|need(?:s)? more|need(?:s)?\s+to\s+buy|we need)\b/i,
      /\bbuy\s+(some|more)?\s*([a-z][a-z ]+)/i,
    ],
  },

  // ── MEALS / SHOPPING (food specific) ─────────────────────────────
  { module: "meals", category: "Meals", action: "add-shopping", reason: "Looks like a food / ingredient note",
    keywords: [
      "milk", "bread", "eggs", "butter", "cheese", "flour", "sugar",
      "rice", "pasta", "tomato", "onion", "garlic", "cream", "yoghurt",
      "chicken", "mince", "beef", "fish", "bananas", "fruit",
      "baking list", "shopping list",
    ],
  },
  { module: "meals", category: "Meals", action: "add-meal-plan", reason: "Sounds like a dinner plan",
    patterns: [
      /\bdinner\s+(?:is|tonight|will be|tomorrow)\b/i,
      /\b(lunch|breakfast|brunch|tea)\s+(?:is|tonight|today|tomorrow)\b/i,
      /\bmeal plan/i,
    ],
  },

  // ── HEALTH ───────────────────────────────────────────────────────
  { module: "health", category: "Health", action: "create-reminder", reason: "Looks like a health / medical task",
    keywords: [
      "doctor", "gp", "dentist", "dentist", "optometrist", "physio",
      "prescription", "medication", "pharmacy", "antibiotic", "appointment",
      "vaccine", "vaccination", "immunisation", "blood test", "check-up",
    ],
  },

  // ── MONEY ────────────────────────────────────────────────────────
  { module: "money", category: "Money", action: "create-reminder", reason: "Looks like a bill / payment",
    keywords: [
      "bill", "invoice", "pay ", "payment", "subscription", "renew",
      "rates", "rent", "mortgage", "power bill", "electricity bill",
      "water bill", "internet bill", "phone bill", "insurance",
    ],
    patterns: [/\$\s?\d+/, /\b\d{2,4}\s?(dollars|nzd|usd)\b/i],
  },

  // ── DOCUMENTS / ADMIN ────────────────────────────────────────────
  { module: "documents", category: "Documents", action: "add-document", reason: "Sounds like a document / admin item",
    keywords: [
      "passport", "permission slip", "school newsletter", "school form",
      "consent form", "letter", "contract", "policy", "warranty", "receipt",
      "wof", "rego", "registration", "license", "licence",
    ],
  },

  // ── CLEANING ─────────────────────────────────────────────────────
  { module: "cleaning", category: "Cleaning", action: "add-task", reason: "Sounds like a cleaning task",
    keywords: [
      "clean the", "scrub", "vacuum", "mop", "wipe", "tidy",
      "bathroom clean", "kitchen clean", "lounge clean", "dust",
    ],
  },

  // ── MOOD / FAMILY ────────────────────────────────────────────────
  { module: "mood", category: "Mood", action: "add-mood", reason: "Sounds like a feelings / mood note",
    keywords: [
      "feels anxious", "feels sad", "feels angry", "feels overwhelmed",
      "feels tired", "is upset", "is grumpy", "is overstimulated",
      "having a hard time", "big feelings",
    ],
  },

  // ── CALENDAR (catch-all for scheduling) ──────────────────────────
  { module: "calendar", category: "Calendar", action: "create-reminder", reason: "Sounds like a scheduled event",
    patterns: [
      /\b(book|booking|appointment|meeting|drop[- ]off|pick[- ]?up|trip)\b/i,
      /\b(at|by|due)\s+\d{1,2}(:\d{2})?\s?(am|pm)?\b/i,
    ],
  },

  // ── CHORES (default for "todo / task" style) ─────────────────────
  { module: "chores", category: "Chores", action: "add-task", reason: "Looks like a household task",
    keywords: ["chore", "task", "todo", "remind me", "remember to"],
  },
];

const DAY_WORDS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

function extractDate(text) {
  const lower = text.toLowerCase();
  const today = new Date();

  if (lower.includes("today"))    return today.toISOString().slice(0, 10);
  if (lower.includes("tonight"))  return today.toISOString().slice(0, 10);
  if (lower.includes("tomorrow")) {
    const t = new Date(today); t.setDate(t.getDate() + 1);
    return t.toISOString().slice(0, 10);
  }
  if (lower.includes("next week")) {
    const t = new Date(today); t.setDate(t.getDate() + 7);
    return t.toISOString().slice(0, 10);
  }
  for (let i = 0; i < DAY_WORDS.length; i++) {
    if (lower.includes(DAY_WORDS[i])) {
      const t = new Date(today);
      const diff = (i - t.getDay() + 7) % 7 || 7;
      t.setDate(t.getDate() + diff);
      return t.toISOString().slice(0, 10);
    }
  }
  return null;
}

function extractTime(text) {
  const m = text.match(/\b(\d{1,2})(?::(\d{2}))?\s?(am|pm)\b/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const mins = m[2] ? parseInt(m[2], 10) : 0;
  const ampm = m[3].toLowerCase();
  if (ampm === "pm" && h < 12) h += 12;
  if (ampm === "am" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

function extractPerson(text) {
  const lower = text.toLowerCase();
  for (const p of PERSONS) {
    const re = new RegExp(`\\b${p}\\b`, "i");
    if (re.test(lower)) return p;
  }
  return null;
}

function extractItem(text) {
  // Best-effort: pull noun after "need", "buy", "is low", "is out"
  const lower = text.toLowerCase();
  let m;
  m = lower.match(/\b(?:we need|need(?:s)? (?:more|some)?|buy|grab|get)\s+([a-z][a-z\s']{1,40})/);
  if (m) return cleanItem(m[1]);
  m = lower.match(/\b([a-z][a-z\s']{1,40})\s+(?:is|are|'s)\s+(?:low|out|nearly out|running low)/);
  if (m) return cleanItem(m[1]);
  m = lower.match(/\badd\s+([a-z][a-z\s']{1,40})\s+to/);
  if (m) return cleanItem(m[1]);
  return null;
}

function cleanItem(s) {
  return s
    .replace(/^(the|some|more|a|an)\s+/i, "")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());
}

const SUPPLY_HINT_CATEGORY = [
  { cat: "Fridge",   words: ["milk", "yoghurt", "cheese", "butter", "cream", "eggs"] },
  { cat: "Freezer",  words: ["frozen", "ice cream"] },
  { cat: "Cleaning", words: ["dishwasher", "surface spray", "bin liner", "bleach", "wipes"] },
  { cat: "Bathroom", words: ["toilet paper", "shampoo", "toothpaste", "soap", "deodorant"] },
  { cat: "Laundry",  words: ["laundry liquid", "fabric softener", "stain remover"] },
  { cat: "Baby/Pet", words: ["nappies", "nappy", "diaper", "wipes", "formula", "cat food", "cat litter", "dog food"] },
  { cat: "School",   words: ["glue stick", "art supplies", "stationery", "exercise book"] },
  { cat: "Pantry",   words: ["rice", "pasta", "flour", "sugar", "oil", "tomato", "stock", "lentils"] },
];

function detectSupplyCategory(text) {
  const lower = text.toLowerCase();
  for (const hint of SUPPLY_HINT_CATEGORY) {
    if (hint.words.some(w => lower.includes(w))) return hint.cat;
  }
  return "Household";
}

const MODULE_LABEL = {
  supplies:  "Supplies",
  meals:     "Meals",
  money:     "Money",
  health:    "Health",
  documents: "Documents",
  cleaning:  "Cleaning",
  calendar:  "Calendar",
  chores:    "Chores",
  family:    "Family",
  mood:      "Mood",
};

const ACTION_LABEL = {
  "add-supply-low":  "Mark low + add to shopping list",
  "add-shopping":    "Add to shopping list",
  "add-meal-plan":   "Add to meal plan",
  "create-reminder": "Create reminder",
  "add-task":        "Create task",
  "add-document":    "Save as document",
  "add-mood":        "Add mood note",
};

export function routeQuickCaptureInput(input = "", attachments = []) {
  const text = String(input || "").trim();
  const hasAttachments = Array.isArray(attachments) && attachments.length > 0;

  // 1) If there is no text but there's a file/photo → suggest Documents.
  if (!text && hasAttachments) {
    const isImage = attachments.some(a => a.kind === "image");
    return finalise({
      module: "documents",
      category: "Documents",
      action: "add-document",
      confidence: "medium",
      reason: isImage ? "Photo attached — likely a document or receipt" : "File attached",
      extracted: {},
    });
  }

  // 2) Apply rules in priority order
  let match = null;
  for (const rule of RULES) {
    const lower = text.toLowerCase();
    const kwHit = rule.keywords?.some(k => lower.includes(k));
    const reHit = rule.patterns?.some(re => re.test(text));
    if (kwHit || reHit) {
      match = { ...rule, _kwHit: !!kwHit, _reHit: !!reHit };
      break;
    }
  }
  if (!match) {
    // Fallback to chores task
    match = { module: "chores", category: "Chores", action: "add-task", reason: "No clear match — kept as a chore task" };
  }

  const extracted = {
    item: extractItem(text),
    date: extractDate(text),
    time: extractTime(text),
    person: extractPerson(text),
    supplyCategory: match.module === "supplies" ? detectSupplyCategory(text) : null,
  };

  let confidence = "medium";
  if (match._kwHit && match._reHit) confidence = "high";
  else if (match._kwHit) confidence = "high";
  else if (match._reHit) confidence = "medium";
  else confidence = "low";

  // Attachments push us toward documents if module is calendar / chores
  if (hasAttachments && (match.module === "chores")) {
    return finalise({ module: "documents", category: "Documents", action: "add-document", confidence: "medium",
      reason: "Attachment present — saving as document", extracted });
  }

  return finalise({ ...match, confidence, extracted });
}

function finalise({ module, category, action, confidence, reason, extracted }) {
  return {
    module,
    moduleLabel: MODULE_LABEL[module] || "Today",
    category,
    action,
    actionLabel: ACTION_LABEL[action] || "Save to Home OS",
    confidence,
    reason,
    extracted: extracted || {},
  };
}

export const MODULE_OPTIONS = Object.keys(MODULE_LABEL).map(id => ({ id, label: MODULE_LABEL[id] }));
export const ACTION_OPTIONS = Object.keys(ACTION_LABEL).map(id => ({ id, label: ACTION_LABEL[id] }));
